import React, { useState, useEffect, useRef } from 'react';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, isToday,
         addWeeks, subWeeks, parseISO, isSameDay, addMonths, subMonths,
         startOfMonth, endOfMonth, getDay, addDays } from 'date-fns';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_URL, getAuthHeader } from '../utils/config';
import useStore from '../store/useStore';
import MeetingModal from '../components/chat/MeetingModal';

const COLORS = ['#0d9488','#2563eb','#7c3aed','#db2777','#d97706','#059669','#dc2626'];

export default function CalendarPage() {
  const { user, darkMode } = useStore();
  const [view, setView] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('tab') === 'meetings' ? 'meetings' : 'month'; } catch(e) { return 'month'; }
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [showAgenda, setShowAgenda] = useState(true);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingForm, setMeetingForm] = useState({ title:'', description:'', meet_link:'', scheduled_at:'', duration_minutes:30, participant_ids:[] });
  const [meetTab, setMeetTab] = useState('upcoming');
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncEmail, setSyncEmail] = useState('');
  const [syncPassword, setSyncPassword] = useState('');
  const [syncConfigured, setSyncConfigured] = useState(false);
  const [selectedColleague, setSelectedColleague] = useState(null);
  const [colleagueMeetings, setColleagueMeetings] = useState([]);

  // Theme colors - respects user's dark/light mode preference
  const bg      = darkMode ? '#0a0f1a'                        : '#f1f5f9';
  const sidebar  = darkMode ? '#0d1424'                        : '#ffffff';
  const card     = darkMode ? '#111827'                        : '#ffffff';
  const border   = darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.08)';
  const text     = darkMode ? '#f1f5f9'                        : '#0f172a';
  const muted    = darkMode ? '#64748b'                        : '#94a3b8';
  const accent   = '#0d9488';

  const getMeetLink = () => `https://qtalk.qualtechedge.in/meet/${user?.meet_link}`;

  useEffect(() => {
    fetchMeetings();
    fetchUsers();
    fetchCalendarEvents();
    // Check sync status
    axios.get(`${API_URL}/users/me/sync-status`, {headers:getAuthHeader()})
      .then(({data}) => { setSyncConfigured(data.configured); if(data.last_sync) setLastSync(new Date(data.last_sync)); if(data.sync_email) setSyncEmail(data.sync_email); })
      .catch(()=>{});
  }, []);

  useEffect(() => { fetchCalendarEvents(); }, [currentDate]);

  // Auto-open meeting from URL param (e.g. from notification click)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const meetingId = params.get('meeting');
    if (!meetingId) return;
    // Wait for meetings to load then find and open it
    const tryOpen = (attempts=0) => {
      const found = [...meetings, ...events].find(m => String(m.id) === String(meetingId));
      if (found) {
        setSelectedEvent({...found, _type: found.scheduled_at ? 'meeting' : 'external'});
        // Navigate to the right month
        const dt = found.scheduled_at || found.start_at;
        if (dt) setCurrentDate(new Date(dt));
        // Clear URL param
        window.history.replaceState({}, '', '/calendar');
      } else if (attempts < 10) {
        setTimeout(() => tryOpen(attempts+1), 500);
      }
    };
    setTimeout(() => tryOpen(), 800);
  }, []);

  const fetchUsers = async () => {
    try { const {data} = await axios.get(`${API_URL}/users`, {headers:getAuthHeader()}); setAllUsers(data||[]); } catch(e) {}
  };

  const fetchCalendarEvents = async () => {
    try {
      const month = format(currentDate, 'yyyy-MM-01');
      const {data} = await axios.get(`${API_URL}/calendar/my?month=${month}`, {headers:getAuthHeader()});
      const all = [...(data.qtalk||[]), ...(data.external||[])];
      setEvents(all);
    } catch(e) {}
  };

  // Colleague calendar fetch — kept out of the click handler so it can also be
  // re-run when the month changes (see the effect below). Previously this lived
  // inline in onClick, capturing currentDate once, so navigating months left
  // the colleague's events stale.
  const fetchColleagueCalendar = async (u, dateForMonth) => {
    if (!u) return;
    const d = dateForMonth || new Date();
    const monthStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
    try {
      const meetRes = await axios.get(`${API_URL}/meetings/user/${u.id}`, {headers:getAuthHeader()});
      const qtalkMeetings = (meetRes.data||[]).map(m=>({...m,_type:'meeting'}));
      let externalEvents = [];
      try {
        const calRes = await axios.get(`${API_URL}/calendar/my?month=${monthStr}&user_id=${u.id}`, {headers:getAuthHeader()});
        externalEvents = (calRes.data?.external||[]).map(e=>({...e,_type:'event'}));
      } catch(e2) {}
      setColleagueMeetings([...qtalkMeetings,...externalEvents]);
    } catch(e) { setColleagueMeetings([]); }
  };

  // Refetch the colleague's events whenever the viewed month changes.
  useEffect(() => {
    if (selectedColleague) fetchColleagueCalendar(selectedColleague, currentDate);
    // eslint-disable-next-line
  }, [selectedColleague, currentDate]);

  const fetchMeetings = async () => {
    try { const {data} = await axios.get(`${API_URL}/meetings`, {headers:getAuthHeader()}); setMeetings(data||[]); } catch(e) {}
  };

  const respondMeeting = async (id, status) => {
    try {
      await axios.put(`${API_URL}/meetings/${id}/respond`, {status}, {headers:getAuthHeader()});
      toast.success(`Meeting ${status}`);
      setMeetings(prev => prev.map(m => m.id===id ? {...m, my_status:status} : m));
    } catch(e) { toast.error('Failed'); }
  };

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  const openEditModal = (meeting) => {
    // Convert scheduled_at to local datetime-local format
    const dt = new Date(meeting.scheduled_at);
    const pad = n => String(n).padStart(2,'0');
    const local = `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    setEditForm({
      id: meeting.id,
      title: meeting.title || '',
      description: meeting.description || '',
      meet_link: meeting.meet_link || '',
      scheduled_at: local,
      duration_minutes: meeting.duration_minutes || 30,
    });
    setShowEditModal(true);
  };

  const saveMeetingEdit = async () => {
    if (!editForm.title || !editForm.scheduled_at) return toast.error('Title and time are required');
    setEditSaving(true);
    try {
      await axios.put(`${API_URL}/meetings/${editForm.id}`, editForm, {headers:getAuthHeader()});
      toast.success('Meeting updated and participants notified!');
      setShowEditModal(false);
      setSelectedEvent(null);
      fetchMeetings();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed to update'); }
    finally { setEditSaving(false); }
  };

  const cancelMeeting = async (id) => {
    try {
      await axios.delete(`${API_URL}/meetings/${id}`, {headers:getAuthHeader()});
      toast.success('Meeting cancelled and participants notified');
      setCancelConfirm(false);
      setSelectedEvent(null);
      setMeetings(prev => prev.filter(m => m.id !== id));
    } catch(e) { toast.error(e.response?.data?.message || 'Failed to cancel'); }
  };

  const handleMeetingSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/meetings`, {...meetingForm}, {headers:getAuthHeader()});
      toast.success('Meeting scheduled!');
      setShowMeetingModal(false);
      setMeetingForm({title:'',description:'',meet_link:'',scheduled_at:'',duration_minutes:30,participant_ids:[]});
      fetchMeetings(); fetchCalendarEvents();
    } catch(e) { toast.error('Failed to create meeting'); }
  };

  // Get events/meetings for a day
  const getDayItems = (day) => {
    // If viewing a colleague's calendar, show ONLY their meetings
    if (selectedColleague) {
      return colleagueMeetings
        .filter(m => {
          const t = m.scheduled_at || m.start_at;
          if (!t) return false;
          try { return isSameDay(parseISO(t), day); } catch(e) { return false; }
        })
        .map(m => ({...m,
          _type: m.source==='email' ? 'event' : 'meeting',
          _sortTime: m.scheduled_at || m.start_at}))
        .sort((a,b) => new Date(a._sortTime) - new Date(b._sortTime));
    }
    const dayMeetings = meetings.filter(m => isSameDay(parseISO(m.scheduled_at), day));
    // Only show external (email) events from calendar sync — exclude qtalk meetings to avoid duplicates
    const dayEvents = events.filter(e => {
      const t = e.start_at || e.scheduled_at;
      if (!t) return false;
      if (e.source === 'qtalk') return false; // already in dayMeetings
      return isSameDay(parseISO(t), day);
    });
    // Sort all by time
    const allItems = [
      ...dayMeetings.map(m => ({...m, _type:'meeting', _sortTime: m.scheduled_at})),
      ...dayEvents.map(e => ({...e, _type:'event', _sortTime: e.start_at || e.scheduled_at})),
    ].sort((a,b) => new Date(a._sortTime) - new Date(b._sortTime));
    return allItems;
  };

  const selectedDayItems = getDayItems(selectedDay);

  // Month grid
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDow = (getDay(monthStart) + 6) % 7; // Mon=0
  const calDays = [];
  for (let i = 0; i < startDow; i++) calDays.push(addDays(monthStart, -(startDow - i)));
  let d = monthStart;
  while (d <= monthEnd) { calDays.push(d); d = addDays(d, 1); }
  while (calDays.length % 7 !== 0) calDays.push(addDays(calDays[calDays.length-1], 1));

  // Week grid
  const weekStart = startOfWeek(currentDate, {weekStartsOn:1});
  const weekDays = eachDayOfInterval({start:weekStart, end:addDays(weekStart,6)});
  const HOURS = Array.from({length:24},(_,i)=>i);

  // Mini calendar for sidebar
  const miniStart = startOfMonth(currentDate);
  const miniDow = (getDay(miniStart)+6)%7;
  const miniDays = [];
  for(let i=0;i<miniDow;i++) miniDays.push(null);
  let md = miniStart;
  while(md <= endOfMonth(currentDate)) { miniDays.push(md); md = addDays(md,1); }

  const upcoming = meetings.filter(m=>new Date(m.scheduled_at)>=new Date()&&m.my_status!=='declined').sort((a,b)=>new Date(a.scheduled_at)-new Date(b.scheduled_at));
  const past = meetings.filter(m=>new Date(m.scheduled_at)<new Date()).sort((a,b)=>new Date(b.scheduled_at)-new Date(a.scheduled_at));
  const pending = meetings.filter(m=>m.my_status==='pending'&&!m.is_organizer);
  const pendingCount = pending.length;
  const meetList = meetTab==='upcoming'?upcoming:meetTab==='pending'?pending:past;

  return (
    <div style={{display:'flex',height:'100vh',overflow:'hidden',background:bg,color:text,fontFamily:'system-ui,sans-serif'}}>

      {/* LEFT SIDEBAR */}
      <div style={{width:240,flexShrink:0,background:sidebar,borderRight:border,display:'flex',flexDirection:'column',padding:'16px 12px',gap:16,overflowY:'auto'}}>
        {/* Back */}
        <a href="/chat" style={{display:'flex',alignItems:'center',gap:8,color:muted,textDecoration:'none',fontSize:12,fontWeight:600,padding:'4px 8px',borderRadius:8,marginBottom:4}}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}><polyline points="15 18 9 12 15 6"/></svg>
          Back to Qtalk
        </a>

        {/* Create button */}
        <button onClick={() => { setMeetingForm(f=>({...f,meet_link:getMeetLink()})); setShowMeetingModal(true); }}
          style={{width:'100%',padding:'10px',borderRadius:12,border:'none',cursor:'pointer',fontSize:13,fontWeight:700,
            background:'linear-gradient(135deg,#0d9488,#2563eb)',color:'white',display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width:15,height:15}}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Schedule Meeting
        </button>

        {/* Mini calendar */}
        <div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <span style={{fontSize:12,fontWeight:700,color:text}}>{format(currentDate,'MMM yyyy')}</span>
            <div style={{display:'flex',gap:4}}>
              <button onClick={()=>setCurrentDate(subMonths(currentDate,1))} style={{background:'none',border:'none',color:muted,cursor:'pointer',padding:2}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:12,height:12}}><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button onClick={()=>setCurrentDate(addMonths(currentDate,1))} style={{background:'none',border:'none',color:muted,cursor:'pointer',padding:2}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:12,height:12}}><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:1}}>
            {['M','T','W','T','F','S','S'].map((d,i)=>(
              <div key={i} style={{textAlign:'center',fontSize:9,fontWeight:700,color:muted,padding:'2px 0'}}>{d}</div>
            ))}
            {miniDays.map((day,i)=>{
              const hasItems = day && getDayItems(day).length > 0;
              const isSel = day && isSameDay(day, selectedDay);
              const isTod = day && isToday(day);
              return (
                <div key={i} onClick={()=>{ if(day){setSelectedDay(day);setShowAgenda(true);}}}
                  style={{textAlign:'center',padding:'3px 0',fontSize:10,fontWeight:isTod?700:400,borderRadius:5,cursor:day?'pointer':'default',
                    background:isSel?accent:isTod?`${accent}30`:'transparent',
                    color:isSel?'white':isTod?accent:day?text:'transparent'}}>
                  {day?format(day,'d'):''}
                  {hasItems&&!isSel&&<div style={{width:3,height:3,borderRadius:'50%',background:accent,margin:'0 auto'}}/>}
                </div>
              );
            })}
          </div>
        </div>

        {/* View switcher */}
        <div>
          <p style={{fontSize:10,fontWeight:700,color:muted,letterSpacing:'0.06em',textTransform:'uppercase',marginBottom:8}}>Views</p>
          {[
            {id:'month',label:'Month',icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>},
            {id:'week',label:'Week',icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="4" x2="8" y2="20"/><line x1="13" y1="4" x2="13" y2="20"/></svg>},
            {id:'meetings',label:'My Meetings',icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>},
          ].map(v=>(
            <button key={v.id} onClick={()=>setView(v.id)}
              style={{width:'100%',display:'flex',alignItems:'center',gap:8,padding:'8px 10px',borderRadius:9,border:'none',cursor:'pointer',marginBottom:2,fontSize:12,fontWeight:600,textAlign:'left',
                background:view===v.id?`${accent}20`:'transparent',
                color:view===v.id?accent:muted}}>
              {v.icon}
              <span style={{flex:1}}>{v.label}</span>
              {v.id==='meetings' && pendingCount > 0 && (
                <span style={{minWidth:18,height:18,borderRadius:9,background:'#f59e0b',color:'white',fontSize:10,fontWeight:800,display:'flex',alignItems:'center',justifyContent:'center',padding:'0 4px'}}>
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>



        {/* My Calendars */}
        <div>
          <p style={{fontSize:10,fontWeight:700,color:muted,letterSpacing:'0.06em',textTransform:'uppercase',marginBottom:8}}>My Calendars</p>
          {[{label:'Qtalk Meetings',color:accent},{label:'Synced Events',color:'#7c3aed'},{label:'Holidays',color:'#d97706'}].map(c=>(
            <div key={c.label} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0'}}>
              <div style={{width:10,height:10,borderRadius:3,background:c.color,flexShrink:0}}/>
              <span style={{fontSize:11,color:muted}}>{c.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* MAIN AREA */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>

        {/* TOP HEADER */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 20px',borderBottom:border,flexShrink:0,background:sidebar}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <h1 style={{fontSize:18,fontWeight:800,color:text,margin:0}}>
              {view==='meetings'?'My Meetings':format(currentDate, view==='week'?'MMM d – ':'MMMM yyyy')}
              {view==='week'&&format(addDays(weekStart,6),'MMM d, yyyy')}
            </h1>
            {view!=='meetings'&&(
              <div style={{display:'flex',gap:4}}>
                <button onClick={()=>setCurrentDate(view==='week'?subWeeks(currentDate,1):subMonths(currentDate,1))}
                  style={{width:28,height:28,borderRadius:8,border,background:'transparent',color:muted,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <button onClick={()=>setCurrentDate(new Date())}
                  style={{padding:'4px 10px',borderRadius:8,border,background:'transparent',color:muted,cursor:'pointer',fontSize:11,fontWeight:600}}>Today</button>
                <button onClick={()=>setCurrentDate(view==='week'?addWeeks(currentDate,1):addMonths(currentDate,1))}
                  style={{width:28,height:28,borderRadius:8,border,background:'transparent',color:muted,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>
            )}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8,flex:1,maxWidth:600,margin:'0 24px'}}>
            {/* Colleague chip - shown when viewing someone's calendar */}
            {selectedColleague && (
              <div style={{display:'flex',alignItems:'center',gap:6,padding:'4px 8px 4px 6px',borderRadius:20,background:'rgba(13,148,136,0.15)',border:'1px solid rgba(13,148,136,0.3)',flexShrink:0}}>
                <div style={{width:18,height:18,borderRadius:'50%',background:`linear-gradient(135deg,${accent},#2563eb)`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,fontWeight:700,color:'white',overflow:'hidden',flexShrink:0}}>
                  {selectedColleague.avatar_url?.startsWith('/uploads/avatars')
                    ?<img src={`https://qtalk.qualtechedge.in${selectedColleague.avatar_url}`} style={{width:'100%',height:'100%',objectFit:'cover'}} alt=""/>
                    :selectedColleague.full_name[0]?.toUpperCase()}
                </div>
                <span style={{fontSize:11,fontWeight:600,color:accent,whiteSpace:'nowrap'}}>{selectedColleague.full_name}</span>
                <button onClick={()=>{setSelectedColleague(null);setColleagueMeetings([]);}}
                  style={{background:'none',border:'none',color:accent,cursor:'pointer',padding:0,display:'flex',alignItems:'center',marginLeft:2}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width:12,height:12}}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            )}
            {/* Search bar */}
            <div style={{flex:1,position:'relative'}}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:13,height:13,position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:muted}}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input placeholder="Search colleagues..." value={userSearch} onChange={e=>{setUserSearch(e.target.value);setSearchResults(e.target.value?allUsers.filter(u=>u.full_name.toLowerCase().includes(e.target.value.toLowerCase())).slice(0,5):[]);}}
                style={{width:'100%',background:darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.04)',border,borderRadius:10,padding:'7px 10px 7px 30px',color:text,fontSize:12,outline:'none',boxSizing:'border-box'}}/>
              {searchResults.length>0&&(
                <div style={{position:'absolute',top:'100%',left:0,right:0,background:card,border,borderRadius:10,zIndex:100,marginTop:4,overflow:'hidden',boxShadow:darkMode?'0 8px 24px rgba(0,0,0,0.4)':'0 8px 24px rgba(0,0,0,0.15)'}}>
                  {searchResults.map(u=>(
                    <div key={u.id} onClick={async()=>{
                      setUserSearch(''); setSearchResults([]);
                      setSelectedColleague(u);
                      setShowAgenda(true);
                      setSelectedDay(new Date());
                      try {
                        await fetchColleagueCalendar(u, currentDate);
                        setShowAgenda(true);
                        toast.success(`Viewing ${u.full_name}'s calendar`);
                      } catch(e){ setColleagueMeetings([]); toast.error('Could not load calendar'); }
                    }} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',cursor:'pointer',borderBottom:border}}>
                      <div style={{width:28,height:28,borderRadius:'50%',background:`linear-gradient(135deg,${accent},#2563eb)`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:'white',flexShrink:0,overflow:'hidden'}}>
                        {u.avatar_url?.startsWith('/uploads/avatars')?<img src={`https://qtalk.qualtechedge.in${u.avatar_url}`} style={{width:'100%',height:'100%',objectFit:'cover'}} alt=""/>:u.full_name[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p style={{fontSize:12,fontWeight:600,color:text,margin:0}}>{u.full_name}</p>
                        <p style={{fontSize:10,color:muted,margin:0}}>{u.designation||u.department||u.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Sync button */}
            <button onClick={async()=>{
              if(syncConfigured){
                setSyncing(true);
                try {
                  await axios.post(`${API_URL}/users/me/sync-email/trigger`,{},{headers:getAuthHeader()});
                  setLastSync(new Date()); toast.success('Syncing calendar...');
                  setTimeout(()=>{ fetchCalendarEvents(); setSyncing(false); },4000);
                } catch(e){ setSyncing(false); setShowSyncModal(true); }
              } else { setShowSyncModal(true); }
            }} style={{display:'flex',alignItems:'center',gap:6,padding:'7px 12px',borderRadius:10,border,
              background:syncConfigured?`${accent}15`:'transparent',color:syncConfigured?accent:muted,cursor:'pointer',fontSize:11,fontWeight:600,flexShrink:0}}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:13,height:13,animation:syncing?'spin 1s linear infinite':'none'}}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
              {syncing?'Syncing...':syncConfigured?`Synced${lastSync?' · '+format(lastSync,'HH:mm'):''}` : 'Setup Sync'}
            </button>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            {view==='meetings'&&(
              <div style={{display:'flex',gap:3,background:darkMode?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.05)',borderRadius:10,padding:3}}>
                {[{id:'upcoming',label:'Upcoming'},{id:'pending',label:'Invites'},{id:'past',label:'Past'}].map(t=>(
                  <button key={t.id} onClick={()=>setMeetTab(t.id)}
                    style={{padding:'5px 12px',borderRadius:8,border:'none',cursor:'pointer',fontSize:11,fontWeight:600,
                      background:meetTab===t.id?'linear-gradient(135deg,#0d9488,#2563eb)':'transparent',
                      color:meetTab===t.id?'white':muted}}>
                    {t.label}
                  </button>
                ))}
              </div>
            )}
            <div style={{display:'flex',alignItems:'center',gap:2,background:darkMode?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.05)',borderRadius:10,padding:3}}>
              {['month','week'].map(v=>(
                <button key={v} onClick={()=>setView(v)}
                  style={{padding:'5px 10px',borderRadius:8,border:'none',cursor:'pointer',fontSize:11,fontWeight:600,
                    background:view===v?'rgba(13,148,136,0.3)':'transparent',color:view===v?accent:muted}}>
                  {v.charAt(0).toUpperCase()+v.slice(1)}
                </button>
              ))}
            </div>
            {view!=='meetings'&&(
              <button onClick={()=>setShowAgenda(!showAgenda)}
                style={{padding:'6px 12px',borderRadius:9,border,background:'transparent',color:muted,cursor:'pointer',fontSize:11,fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:13,height:13}}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                Agenda
              </button>
            )}
            <div style={{width:32,height:32,borderRadius:'50%',background:`linear-gradient(135deg,${accent},#2563eb)`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700,color:'white',cursor:'pointer',overflow:'hidden',flexShrink:0}}>
              {user?.avatar_url && user.avatar_url.length > 0
                ? <img src={user.avatar_url.startsWith('http') ? user.avatar_url : `https://qtalk.qualtechedge.in${user.avatar_url}`} style={{width:'100%',height:'100%',objectFit:'cover'}} alt="" onError={e=>{e.target.style.display='none';}}/>
                : user?.full_name?.[0]?.toUpperCase()}
            </div>
          </div>
        </div>

        {/* CONTENT */}
        <div style={{flex:1,display:'flex',overflow:'hidden'}}>

          {/* MONTH VIEW */}
          {view==='month'&&(
            <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
              {/* Day headers */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',borderBottom:border,flexShrink:0}}>
                {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d,di)=>(
                  <div key={d} style={{padding:'8px 12px',fontSize:11,fontWeight:700,
                    color:di>=5?'#f59e0b':muted,textAlign:'center',letterSpacing:'0.05em'}}>{d}</div>
                ))}
              </div>
              {/* Grid */}
              <div style={{flex:1,display:'grid',gridTemplateColumns:'repeat(7,1fr)',gridTemplateRows:`repeat(${calDays.length/7},1fr)`,overflow:'hidden'}}>
                {calDays.map((day,i)=>{
                  const items = getDayItems(day);
                  const inMonth = day >= monthStart && day <= monthEnd;
                  const isSel = isSameDay(day, selectedDay);
                  const isTod = isToday(day);
                  const dow = getDay(day); // 0=Sun, 6=Sat
                  const isWeekend = dow === 0 || dow === 6;
                  return (
                    <div key={i} onClick={()=>{setSelectedDay(day);setShowAgenda(true);}}
                      style={{borderRight:i%7<6?border:'none',borderBottom:border,padding:'6px 8px',cursor:'pointer',overflow:'hidden',
                        background:isSel?`${accent}12`:isTod?`${accent}08`:isWeekend&&inMonth?'rgba(245,158,11,0.04)':'transparent',
                        opacity:inMonth?1:0.35,transition:'background 0.15s'}}>
                      <div style={{width:22,height:22,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:3,
                        background:isTod?accent:'transparent',color:isTod?'white':isSel?accent:text,
                        fontSize:12,fontWeight:isTod||isSel?700:400}}>
                        {format(day,'d')}
                      </div>
                      {items.slice(0,3).map((item,j)=>{
                        const isPending = item._type==='meeting' && item.my_status==='pending' && !item.is_organizer;
                        return (
                          <div key={j} onClick={e=>{e.stopPropagation();setSelectedEvent(item);}}
                            style={{fontSize:10,fontWeight:600,padding:'1px 6px',borderRadius:4,marginBottom:1,
                              background:isPending?'rgba(245,158,11,0.2)':item._type==='meeting'?`${accent}25`:item._type==='colleague'?'rgba(239,68,68,0.15)':item.source==='email'?'rgba(124,58,237,0.2)':'rgba(255,255,255,0.06)',
                              color:isPending?'#f59e0b':item._type==='meeting'?accent:item._type==='colleague'?'#ef4444':item.source==='email'?'#a78bfa':muted,
                              whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',cursor:'pointer',
                              border:isPending?'1px solid rgba(245,158,11,0.4)':'none',
                              display:'flex',alignItems:'center',gap:3}}>
                            {isPending&&<span style={{width:5,height:5,borderRadius:'50%',background:'#f59e0b',flexShrink:0,display:'inline-block'}}/>}
                            {item._type==='colleague' ? `${selectedColleague?.full_name?.split(' ')[0]}: Busy` : selectedColleague ? '🔒 Busy' : item.title}
                          </div>
                        );
                      })}
                      {items.length>3&&<div style={{fontSize:9,color:muted,fontWeight:600}}>+{items.length-3} more</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* WEEK VIEW */}
          {view==='week'&&(
            <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
              <div style={{display:'grid',gridTemplateColumns:'48px repeat(7,1fr)',borderBottom:border,flexShrink:0}}>
                <div/>
                {weekDays.map(day=>(
                  <div key={day.toString()} onClick={()=>{setSelectedDay(day);setShowAgenda(true);}}
                    style={{padding:'8px 0',textAlign:'center',cursor:'pointer',borderLeft:border}}>
                    <p style={{fontSize:10,fontWeight:600,color:muted,margin:0}}>{format(day,'EEE').toUpperCase()}</p>
                    <div style={{width:28,height:28,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',margin:'2px auto 0',
                      background:isToday(day)?accent:'transparent',color:isToday(day)?'white':text,fontSize:14,fontWeight:700}}>
                      {format(day,'d')}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{flex:1,overflowY:'auto',display:'grid',gridTemplateColumns:'48px repeat(7,1fr)'}}>
                {HOURS.map(h=>(
                  <React.Fragment key={h}>
                    <div style={{fontSize:9,color:muted,textAlign:'right',paddingRight:6,paddingTop:2,borderTop:h>0?border:'none'}}>{h>0?`${h}:00`:''}</div>
                    {weekDays.map(day=>{
                      const hourItems = getDayItems(day).filter(item=>{
                        const itemH = new Date(item.scheduled_at||item.start_at).getHours();
                        return itemH===h;
                      });
                      return (
                        <div key={day.toString()} style={{borderLeft:border,borderTop:h>0?border:'none',minHeight:48,padding:2,position:'relative',background:isToday(day)?`${accent}04`:'transparent'}}>
                          {hourItems.map((item,j)=>(
                            <div key={j} onClick={()=>setSelectedEvent(item)}
                              style={{fontSize:10,fontWeight:600,padding:'2px 5px',borderRadius:4,marginBottom:1,cursor:'pointer',
                                background:item._type==='meeting'?`${accent}30`:'rgba(124,58,237,0.2)',
                                color:item._type==='meeting'?accent:'#a78bfa',
                                borderLeft:`2px solid ${item._type==='meeting'?accent:'#7c3aed'}`}}>
                              {selectedColleague ? '🔒 Busy' : item.title}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* MEETINGS VIEW */}
          {view==='meetings'&&(
            <div style={{flex:1,overflowY:'auto',padding:'24px'}}>
              {/* Stats */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:24}}>
                {[
                  {label:'Total',count:meetings.length,color:accent},
                  {label:'Upcoming',count:upcoming.length,color:'#2563eb'},
                  {label:'Pending Invites',count:pending.length,color:'#f59e0b'},
                ].map(s=>(
                  <div key={s.label} style={{background:card,border,borderRadius:14,padding:'16px 20px',display:'flex',alignItems:'center',gap:12}}>
                    <div style={{width:42,height:42,borderRadius:10,background:`${s.color}18`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      <span style={{fontSize:20,fontWeight:800,color:s.color}}>{s.count}</span>
                    </div>
                    <p style={{fontSize:12,color:muted,fontWeight:600,margin:0}}>{s.label}</p>
                  </div>
                ))}
              </div>
              {/* Meeting list */}
              {meetList.length===0?(
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'60px 0',gap:16}}>
                  <div style={{width:64,height:64,borderRadius:18,background:`${accent}12`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <svg viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{width:32,height:32,opacity:0.5}}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </div>
                  <p style={{fontSize:15,fontWeight:600,color:text}}>{meetTab==='upcoming'?'No upcoming meetings':meetTab==='pending'?'No pending invites':'No past meetings'}</p>
                  {meetTab==='upcoming'&&(
                    <button onClick={()=>{setMeetingForm(f=>({...f,meet_link:getMeetLink()}));setShowMeetingModal(true);}}
                      style={{padding:'10px 24px',borderRadius:12,border:'none',background:`linear-gradient(135deg,${accent},#2563eb)`,color:'white',cursor:'pointer',fontSize:13,fontWeight:700}}>
                      Schedule a Meeting
                    </button>
                  )}
                </div>
              ):meetList.map(m=>{
                const md = parseISO(m.scheduled_at);
                const isFuture = new Date(m.scheduled_at)>=new Date();
                return (
                  <div key={m.id} onClick={()=>setSelectedEvent({...m,_type:'meeting'})}
                    style={{background:card,border:isToday(md)?`1.5px solid ${accent}`:border,borderRadius:14,padding:'14px 18px',marginBottom:8,display:'flex',alignItems:'center',gap:14,cursor:'pointer',transition:'background 0.15s'}}
                    onMouseEnter={e=>e.currentTarget.style.background=darkMode?'rgba(255,255,255,0.04)':'rgba(0,0,0,0.03)'}
                    onMouseLeave={e=>e.currentTarget.style.background=card}>
                    <div style={{width:48,height:48,borderRadius:12,flexShrink:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
                      background:isToday(md)?`linear-gradient(135deg,${accent},#2563eb)`:isFuture?'rgba(37,99,235,0.15)':darkMode?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.06)',color:isToday(md)||isFuture?'white':text}}>
                      <span style={{fontSize:'8px',lineHeight:1,fontWeight:700,opacity:0.8}}>{format(md,'MMM').toUpperCase()}</span>
                      <span style={{fontSize:'20px',lineHeight:1.1,fontWeight:800}}>{format(md,'d')}</span>
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3}}>
                        <p style={{fontWeight:700,fontSize:14,color:text,margin:0}}>{m.title}</p>
                        <span style={{fontSize:10,padding:'2px 8px',borderRadius:20,fontWeight:600,
                          background:m.is_organizer?`${accent}18`:m.my_status==='accepted'?'rgba(16,185,129,0.12)':'rgba(245,158,11,0.12)',
                          color:m.is_organizer?accent:m.my_status==='accepted'?'#10b981':'#f59e0b'}}>
                          {m.is_organizer?'Organizer':m.my_status==='accepted'?'Accepted':'Pending'}
                        </span>
                      </div>
                      <p style={{fontSize:12,color:muted,margin:0}}>{new Date(m.scheduled_at).toLocaleDateString('en-IN',{weekday:'short',month:'short',day:'numeric',timeZone:'Asia/Kolkata'})} · {new Date(m.scheduled_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:false,timeZone:'Asia/Kolkata'})} · {m.duration_minutes} min · {m.is_organizer?'You organized':`By ${m.organizer_name}`}</p>
                      {meetTab==='pending'&&(
                        <div style={{marginTop:8}}>
                          {m.description&&<p style={{fontSize:11,color:muted,marginBottom:6,fontStyle:'italic'}}>{m.description}</p>}
                          <div style={{display:'flex',gap:6}}>
                            <button onClick={e=>{e.stopPropagation();respondMeeting(m.id,'accepted');}} style={{padding:'5px 14px',borderRadius:8,border:'none',background:`linear-gradient(135deg,${accent},#2563eb)`,color:'white',cursor:'pointer',fontSize:11,fontWeight:700}}>✓ Accept</button>
                            <button onClick={e=>{e.stopPropagation();respondMeeting(m.id,'declined');}} style={{padding:'5px 14px',borderRadius:8,border:'none',background:'rgba(239,68,68,0.15)',color:'#ef4444',cursor:'pointer',fontSize:11,fontWeight:700}}>✗ Decline</button>
                            {m.meet_link&&<a href={m.meet_link.startsWith('http')?m.meet_link:`https://qtalk.qualtechedge.in${m.meet_link}`} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{padding:'5px 14px',borderRadius:8,background:darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.05)',color:muted,textDecoration:'none',fontSize:11,fontWeight:600}}>👁 Preview</a>}
                          </div>
                        </div>
                      )}
                    </div>
                    {m.meet_link&&isFuture&&(
                      <a href={m.meet_link.startsWith('http')?m.meet_link:`https://qtalk.qualtechedge.in${m.meet_link}`} target="_blank" rel="noreferrer"
                        onClick={e=>e.stopPropagation()}
                        style={{padding:'7px 14px',borderRadius:9,background:`linear-gradient(135deg,${accent},#2563eb)`,color:'white',textDecoration:'none',fontSize:12,fontWeight:700,flexShrink:0,display:'flex',alignItems:'center',gap:5}}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:13,height:13}}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                        Join
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* AGENDA PANEL */}
          {showAgenda && view!=='meetings' && (
            <div style={{width:300,flexShrink:0,borderLeft:border,display:'flex',flexDirection:'column',overflow:'hidden',background:sidebar}}>
              <div style={{padding:'14px 16px',borderBottom:border,flexShrink:0}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <p style={{fontSize:13,fontWeight:700,color:text,margin:0}}>Agenda</p>
                  <button onClick={()=>setShowAgenda(false)} style={{background:'none',border:'none',color:muted,cursor:'pointer',padding:2}}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
                <p style={{fontSize:11,color:muted,margin:'2px 0 0',fontWeight:600}}>
                  {format(selectedDay,'EEEE, MMMM d')}
                  {selectedDayItems.length>0&&<span style={{marginLeft:6,background:accent,color:'white',borderRadius:10,padding:'1px 7px',fontSize:10,fontWeight:700}}>{selectedDayItems.length}</span>}
                </p>
              </div>
              <div style={{flex:1,overflowY:'auto',padding:'12px'}}>
                {selectedDayItems.length===0?(
                  <div style={{textAlign:'center',paddingTop:40}}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{width:32,height:32,color:muted,opacity:0.3,margin:'0 auto 8px',display:'block'}}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    <p style={{fontSize:12,color:muted}}>{selectedColleague?`No events for ${selectedColleague.full_name.split(' ')[0]}`:'No events'} on this day</p>
                    <p style={{fontSize:11,color:muted,opacity:0.6}}>Click another day to view</p>
                    <button onClick={()=>{setMeetingForm(f=>({...f,meet_link:getMeetLink()}));setShowMeetingModal(true);}}
                      style={{marginTop:12,padding:'6px 14px',borderRadius:8,border:`1px solid ${accent}40`,background:'transparent',color:accent,cursor:'pointer',fontSize:11,fontWeight:600}}>
                      + Add meeting
                    </button>
                  </div>
                ):selectedDayItems.map((item,i)=>{
                  const t = item.scheduled_at||item.start_at;
                  return (
                    <div key={i} onClick={()=>setSelectedEvent(item)} style={{background:card,border,borderRadius:12,padding:'12px',marginBottom:8,borderLeft:`3px solid ${item._type==='meeting'?accent:'#7c3aed'}`,cursor:'pointer',transition:'background 0.15s'}} onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.06)'} onMouseLeave={e=>e.currentTarget.style.background=card}>
                      <p style={{fontSize:11,color:item._type==='meeting'?accent:'#a78bfa',fontWeight:700,margin:'0 0 3px'}}>{t?new Date(t).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:false,timeZone:'Asia/Kolkata'}):''}</p>
                       <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                        <p style={{fontSize:13,fontWeight:700,color:text,margin:0}}>{selectedColleague ? '🔒 Busy' : item.title}</p>
                        {!selectedColleague&&<span style={{fontSize:9,color:muted,opacity:0.5}}>tap for details</span>}
                      </div>
                      {!selectedColleague&&(item.duration_minutes||item.organizer||item.organizer_name)&&(
                        <p style={{fontSize:10,color:muted,margin:'0 0 6px'}}>
                          {item.duration_minutes?`${item.duration_minutes} min`:''}{(item.organizer_name||item.organizer)?` · From ${item.organizer_name||item.organizer}`:''}
                          {item.location?` · 📍 ${item.location}`:''}
                        </p>
                      )}
                      {!selectedColleague&&(item.meet_link||item.location?.startsWith('http'))&&(
                        <a href={(item.meet_link||item.location).startsWith('http')?(item.meet_link||item.location):`https://qtalk.qualtechedge.in${item.meet_link}`} target="_blank" rel="noreferrer"
                          onClick={e=>e.stopPropagation()}
                          style={{display:'inline-flex',alignItems:'center',gap:4,padding:'4px 10px',borderRadius:7,background:`${accent}20`,color:accent,textDecoration:'none',fontSize:10,fontWeight:700}}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:10,height:10}}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                          Join
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>

      {/* Sync Modal */}
      {showSyncModal&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',backdropFilter:'blur(4px)',zIndex:50,display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={()=>setShowSyncModal(false)}>
          <div style={{background:card,borderRadius:20,padding:'28px',width:420,border,boxShadow:'0 20px 60px rgba(0,0,0,0.5)'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
              <div>
                <p style={{fontSize:16,fontWeight:800,color:text,margin:0}}>{syncConfigured?'Email Sync Settings':'Sync Email Calendar'}</p>
                <p style={{fontSize:12,color:muted,margin:'4px 0 0'}}>{syncConfigured?`Connected: ${syncEmail}`:'Connect your Yahoo/Gmail to see calendar invites'}</p>
              </div>
              <button onClick={()=>setShowSyncModal(false)} style={{background:'none',border:'none',color:muted,cursor:'pointer'}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {syncConfigured && (
              <div style={{background:'rgba(13,148,136,0.1)',border:'1px solid rgba(13,148,136,0.25)',borderRadius:12,padding:'12px 14px',marginBottom:16,display:'flex',alignItems:'center',gap:10}}>
                <div style={{width:8,height:8,borderRadius:'50%',background:accent,flexShrink:0}}/>
                <div style={{flex:1}}>
                  <p style={{fontSize:13,fontWeight:600,color:accent,margin:0}}>Sync active</p>
                  <p style={{fontSize:11,color:muted,margin:'2px 0 0'}}>{lastSync?`Last synced ${lastSync.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})} · ${lastSync.toLocaleDateString('en-IN',{day:'numeric',month:'short'})}`:'Not synced yet'}</p>
                </div>
                <button onClick={async()=>{
                  setSyncing(true);
                  try {
                    await axios.post(`${API_URL}/users/me/sync-email/trigger`,{},{headers:getAuthHeader()});
                    setTimeout(async()=>{ await fetchCalendarEvents(); setLastSync(new Date()); setSyncing(false); toast.success('Calendar synced!'); },3000);
                  } catch(e){ setSyncing(false); toast.error('Sync failed'); }
                }} style={{background:`linear-gradient(135deg,${accent},#2563eb)`,border:'none',borderRadius:8,padding:'6px 14px',color:'white',fontSize:12,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:6}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:12,height:12,animation:syncing?'spin 1s linear infinite':'none'}}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                  {syncing?'Syncing...':'Sync now'}
                </button>
              </div>
            )}

            <div style={{marginBottom:12}}>
              <label style={{fontSize:11,fontWeight:700,color:muted,display:'block',marginBottom:4,textTransform:'uppercase',letterSpacing:'0.05em'}}>Email Address</label>
              <input value={syncEmail} onChange={e=>setSyncEmail(e.target.value)} placeholder="your@qualtechedge.com"
                style={{width:'100%',background:darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.04)',border,borderRadius:10,padding:'10px 12px',color:text,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
            </div>
            <div style={{marginBottom:8}}>
              <label style={{fontSize:11,fontWeight:700,color:muted,display:'block',marginBottom:4,textTransform:'uppercase',letterSpacing:'0.05em'}}>App Password</label>
              <input type="password" value={syncPassword} onChange={e=>setSyncPassword(e.target.value)} placeholder={syncConfigured?'Enter new app password to update':'Yahoo app password'}
                style={{width:'100%',background:darkMode?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.04)',border,borderRadius:10,padding:'10px 12px',color:text,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
            </div>
            <p style={{fontSize:11,color:muted,marginBottom:16}}>Use an app-specific password, not your regular password. <a href="https://login.yahoo.com/account/security" target="_blank" rel="noreferrer" style={{color:accent}}>Get Yahoo app password →</a></p>
            <div style={{display:'flex',gap:10}}>
              {syncConfigured&&(
                <button onClick={async()=>{
                  if(!window.confirm('Remove email sync?')) return;
                  await axios.delete(`${API_URL}/users/me/sync-email`,{headers:getAuthHeader()});
                  setSyncConfigured(false); setSyncEmail(''); setSyncPassword(''); setShowSyncModal(false);
                  toast.success('Sync removed');
                }} style={{flex:1,padding:'11px',borderRadius:12,border:'1px solid rgba(239,68,68,0.3)',cursor:'pointer',fontSize:13,fontWeight:600,background:'transparent',color:'#ef4444'}}>
                  Remove sync
                </button>
              )}
              <button onClick={async()=>{
                if(!syncEmail||!syncPassword)return toast.error('Enter email and password');
                setSyncing(true);
                try {
                  // Step 1: Save & validate credentials (backend checks IMAP)
                  await axios.post(`${API_URL}/users/me/sync-email`,{email:syncEmail,password:syncPassword},{headers:getAuthHeader()});
                  setSyncConfigured(true);
                  setShowSyncModal(false);
                  toast('🔄 Credentials saved! Starting full calendar scan (may take 2-3 min)...', {duration:6000});
                  // Step 2: Trigger background sync
                  await axios.post(`${API_URL}/users/me/sync-email/trigger`,{},{headers:getAuthHeader(),timeout:30000});
                  // Step 3: Poll every 5s until sync completes
                  const startTime = Date.now();
                  const poll = setInterval(async () => {
                    try {
                      const s = await axios.get(`${API_URL}/users/me/sync-email/status`,{headers:getAuthHeader()});
                      if(s.data.last_sync && new Date(s.data.last_sync) > new Date(startTime - 5000)) {
                        clearInterval(poll); setSyncing(false);
                        await fetchCalendarEvents();
                        setLastSync(new Date(s.data.last_sync));
                        toast.success(`✅ Calendar synced! ${s.data.total_events} events imported`);
                      }
                    } catch(e){ clearInterval(poll); setSyncing(false); }
                  }, 5000);
                  setTimeout(()=>{ clearInterval(poll); setSyncing(false); fetchCalendarEvents(); }, 300000);
                } catch(e){
                  const msg = e.response?.data?.message || 'Failed. Check your app password.';
                  toast.error(msg); setSyncing(false);
                  if(e.response?.status===401) setSyncConfigured(false);
                }
              }} disabled={syncing||!syncEmail||!syncPassword}
                style={{flex:2,padding:'11px',borderRadius:12,border:'none',cursor:'pointer',fontSize:13,fontWeight:700,
                  background:syncing?darkMode?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.1)':`linear-gradient(135deg,${accent},#2563eb)`,color:'white',
                  display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,animation:syncing?'spin 1s linear infinite':'none'}}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
                {syncing?'Syncing...':syncConfigured?'Update & Sync':'Save & Sync Now'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Meeting Modal */}
      <MeetingModal show={showMeetingModal} onClose={()=>setShowMeetingModal(false)}
        meetingForm={meetingForm} setMeetingForm={setMeetingForm}
        onSubmit={handleMeetingSubmit} users={allUsers}/>

      {/* Event detail */}
      {selectedEvent&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',backdropFilter:'blur(4px)',zIndex:50,display:'flex',alignItems:'center',justifyContent:'center'}}
          onClick={()=>setSelectedEvent(null)}>
          <div style={{background:card,borderRadius:20,padding:'24px',width:440,maxHeight:'88vh',overflowY:'auto',border,boxShadow:'0 20px 60px rgba(0,0,0,0.5)',scrollbarWidth:'thin'}} onClick={e=>e.stopPropagation()}>
            {/* Header */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
              <span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:20,
                background:selectedEvent._type==='meeting'?`${accent}20`:'rgba(124,58,237,0.15)',
                color:selectedEvent._type==='meeting'?accent:'#a78bfa',textTransform:'uppercase',letterSpacing:'0.06em'}}>
                {selectedEvent._type==='meeting'?'Qtalk Meeting':'External Event'}
              </span>
              <button onClick={()=>setSelectedEvent(null)} style={{background:'none',border:'none',color:muted,cursor:'pointer',padding:4}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:16,height:16}}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Title */}
            <p style={{fontSize:20,fontWeight:800,color:text,marginBottom:16,lineHeight:1.3}}>{selectedColleague ? '🔒 Busy' : selectedEvent.title}</p>

            {/* Time */}
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,padding:'10px 12px',background:'rgba(255,255,255,0.04)',borderRadius:10}}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,color:accent,flexShrink:0}}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <div>
                <p style={{fontSize:13,color:text,fontWeight:600,margin:0}}>
                  {selectedEvent.scheduled_at
                    ? new Date(selectedEvent.scheduled_at).toLocaleDateString('en-IN',{weekday:'short',month:'short',day:'numeric',timeZone:'Asia/Kolkata'}) + ' · ' + new Date(selectedEvent.scheduled_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:false,timeZone:'Asia/Kolkata'})
                    : selectedEvent.start_at
                    ? new Date(selectedEvent.start_at).toLocaleDateString('en-IN',{weekday:'short',month:'short',day:'numeric',timeZone:'Asia/Kolkata'}) + ' · ' + new Date(selectedEvent.start_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',hour12:false,timeZone:'Asia/Kolkata'})
                    : ''}
                  {selectedEvent.duration_minutes?` · ${selectedEvent.duration_minutes} min`:''}
                  {selectedEvent.end_at&&!selectedEvent.duration_minutes?` – ${format(parseISO(selectedEvent.end_at),'HH:mm')}`:''}
                </p>
              </div>
            </div>

            {/* Organizer / Location / Description / Attendees / Participants / Join — hidden when viewing colleague's calendar */}
            {!selectedColleague&&(selectedEvent.organizer_name||selectedEvent.organizer)&&(
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,padding:'10px 12px',background:'rgba(255,255,255,0.04)',borderRadius:10}}>
                <div style={{width:26,height:26,borderRadius:'50%',background:selectedEvent._type==='meeting'?`${accent}30`:'rgba(124,58,237,0.25)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:selectedEvent._type==='meeting'?accent:'#a78bfa',flexShrink:0}}>
                  {(selectedEvent.organizer_name||selectedEvent.organizer)[0]?.toUpperCase()}
                </div>
                <div>
                  <p style={{fontSize:11,color:muted,margin:0}}>Organized by</p>
                  <p style={{fontSize:13,color:text,fontWeight:600,margin:0}}>{selectedEvent.organizer_name||selectedEvent.organizer}</p>
                </div>
              </div>
            )}

            {/* Location — clickable if URL */}
            {!selectedColleague&&selectedEvent.location&&(
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,padding:'10px 12px',background:'rgba(255,255,255,0.04)',borderRadius:10}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,color:accent,flexShrink:0}}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {selectedEvent.location.startsWith('http')
                  ? <a href={selectedEvent.location} target="_blank" rel="noreferrer"
                      style={{fontSize:13,color:accent,margin:0,wordBreak:'break-all',textDecoration:'underline',textUnderlineOffset:2}}>
                      {selectedEvent.location.includes('meet.google.com')?'Join Google Meet →':selectedEvent.location}
                    </a>
                  : <p style={{fontSize:13,color:text,margin:0}}>{selectedEvent.location}</p>
                }
              </div>
            )}

            {/* Description */}
            {!selectedColleague&&selectedEvent.description&&(
              <div style={{marginBottom:12,padding:'10px 12px',background:'rgba(255,255,255,0.04)',borderRadius:10}}>
                <p style={{fontSize:11,color:muted,marginBottom:4}}>Description</p>
                <p style={{fontSize:13,color:text,lineHeight:1.6,margin:0}}>{selectedEvent.description}</p>
              </div>
            )}

            {/* Attendees for external/synced events */}
            {!selectedColleague&&selectedEvent.attendees&&selectedEvent.attendees.length>0&&(
              <div style={{marginBottom:16,padding:'10px 12px',background:'rgba(255,255,255,0.04)',borderRadius:10}}>
                <p style={{fontSize:11,color:muted,marginBottom:8}}>Guests ({selectedEvent.attendees.length})</p>
                <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:160,overflowY:'auto'}}>
                  {selectedEvent.attendees.map((a,i)=>{
                    const name = a.name || a.email?.split('@')[0] || 'Guest';
                    const colors=['#0d9488','#2563eb','#7c3aed','#db2777','#ea580c'];
                    const color = colors[name.charCodeAt(0)%colors.length];
                    return (
                      <div key={i} style={{display:'flex',alignItems:'center',gap:10}}>
                        <div style={{width:28,height:28,borderRadius:'50%',background:color+'25',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color,flexShrink:0}}>
                          {name[0]?.toUpperCase()}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:500,color:text}}>{name}</div>
                          {a.email&&a.email!==name&&<div style={{fontSize:11,color:muted}}>{a.email}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Participants / Invitees */}
            {!selectedColleague&&selectedEvent.participants&&selectedEvent.participants.length>0&&(
              <div style={{marginBottom:16,padding:'10px 12px',background:'rgba(255,255,255,0.04)',borderRadius:10}}>
                <p style={{fontSize:11,color:muted,marginBottom:8}}>Invitees ({selectedEvent.participants.length})</p>
                <div style={{display:'flex',flexDirection:'column',gap:8,maxHeight:200,overflowY:'auto',paddingRight:4,scrollbarWidth:'thin'}}>
                  {selectedEvent.participants.map((p,pi)=>{
                    const name = p.full_name||p.name||p;
                    const isMe = p.id === user?.id;
                    const isOrganizer = p.id === selectedEvent.created_by || p.full_name === selectedEvent.organizer_name;
                    const myStatus = isMe ? p.status : null;
                    const statusColor = p.status==='accepted'?'#10b981':p.status==='declined'?'#ef4444':'#f59e0b';
                    const statusLabel = isOrganizer ? '👑 Organizer' : p.status==='accepted'?'✓ Accepted':p.status==='declined'?'✗ Declined':'⏳ Pending';
                    const statusBgColor = isOrganizer ? accent : statusColor;
                    const avatarUrl = p.avatar_url?.startsWith('/uploads/')?`https://qtalk.qualtechedge.in${p.avatar_url}`:null;
                    return (
                      <div key={pi} style={{display:'flex',alignItems:'center',gap:10}}>
                        <div style={{width:30,height:30,borderRadius:'50%',background:`${accent}25`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,color:accent,flexShrink:0,overflow:'hidden'}}>
                          {avatarUrl?<img src={avatarUrl} style={{width:'100%',height:'100%',objectFit:'cover'}} alt=""/>:name[0]?.toUpperCase()}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:500,color:text}}>{name}{isMe&&<span style={{fontSize:10,color:muted,marginLeft:4}}>(you)</span>}</div>
                          {p.designation&&<div style={{fontSize:11,color:muted}}>{p.designation}</div>}
                        </div>
                        {isMe && !isOrganizer && myStatus !== 'accepted' && myStatus !== 'declined' ? (
                          <div style={{display:'flex',gap:6,flexShrink:0}}>
                            <button onClick={async(e)=>{e.stopPropagation();await respondMeeting(selectedEvent.id,'accepted');setSelectedEvent(prev=>({...prev,participants:prev.participants.map(x=>x.id===p.id?{...x,status:'accepted'}:x)}));}}
                              style={{fontSize:11,padding:'4px 10px',borderRadius:8,border:'none',background:'rgba(16,185,129,0.2)',color:'#10b981',cursor:'pointer',fontWeight:700}}>✓ Accept</button>
                            <button onClick={async(e)=>{e.stopPropagation();await respondMeeting(selectedEvent.id,'declined');setSelectedEvent(prev=>({...prev,participants:prev.participants.map(x=>x.id===p.id?{...x,status:'declined'}:x)}));}}
                              style={{fontSize:11,padding:'4px 10px',borderRadius:8,border:'none',background:'rgba(239,68,68,0.15)',color:'#ef4444',cursor:'pointer',fontWeight:700}}>✗ Decline</button>
                          </div>
                        ) : (
                          <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:statusBgColor+'20',color:statusBgColor,fontWeight:600,flexShrink:0}}>{statusLabel}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Organizer: Edit / Cancel buttons */}
            {!selectedColleague && selectedEvent._type==='meeting' && selectedEvent.is_organizer && (
              <div style={{display:'flex',gap:8,marginTop:8,marginBottom:4}}>
                <button onClick={()=>openEditModal(selectedEvent)}
                  style={{flex:1,padding:'9px',borderRadius:10,border:`1px solid ${accent}40`,background:`${accent}12`,color:accent,fontSize:12,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:5}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:13,height:13}}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  Edit Meeting
                </button>
                <button onClick={()=>setCancelConfirm(true)}
                  style={{flex:1,padding:'9px',borderRadius:10,border:'1px solid rgba(239,68,68,0.3)',background:'rgba(239,68,68,0.08)',color:'#ef4444',fontSize:12,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:5}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:13,height:13}}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
                  Cancel Meeting
                </button>
              </div>
            )}

            {/* Cancel confirm */}
            {!selectedColleague && cancelConfirm && (
              <div style={{background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.25)',borderRadius:10,padding:'12px 14px',marginTop:4}}>
                <p style={{fontSize:13,color:'#ef4444',fontWeight:600,margin:'0 0 10px'}}>Cancel this meeting?</p>
                <p style={{fontSize:11,color:muted,margin:'0 0 12px'}}>All participants will be notified by email and in-app.</p>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={()=>cancelMeeting(selectedEvent.id)}
                    style={{flex:1,padding:'8px',borderRadius:8,border:'none',background:'#ef4444',color:'white',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                    Yes, Cancel It
                  </button>
                  <button onClick={()=>setCancelConfirm(false)}
                    style={{flex:1,padding:'8px',borderRadius:8,border:`1px solid ${border.replace('1px solid ','')}`,background:'transparent',color:text,fontSize:12,fontWeight:600,cursor:'pointer'}}>
                    Keep It
                  </button>
                </div>
              </div>
            )}

            {/* Join button */}
            {!selectedColleague&&selectedEvent.meet_link&&(
              <a href={selectedEvent.meet_link.startsWith('http')?selectedEvent.meet_link:`https://qtalk.qualtechedge.in${selectedEvent.meet_link}`} target="_blank" rel="noreferrer"
                style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'11px',borderRadius:12,background:`linear-gradient(135deg,${accent},#2563eb)`,color:'white',textDecoration:'none',fontSize:13,fontWeight:700,marginTop:4}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14}}><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                Join Meeting
              </a>
            )}
          </div>
        </div>
      )}
      {/* ── EDIT MEETING MODAL ── */}
      {showEditModal && (
        <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,0.6)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:card,borderRadius:18,padding:28,width:'100%',maxWidth:460,boxShadow:'0 24px 64px rgba(0,0,0,0.4)',border:border}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
              <h3 style={{fontSize:17,fontWeight:700,color:text,margin:0}}>✏️ Edit Meeting</h3>
              <button onClick={()=>setShowEditModal(false)} style={{background:'none',border:'none',cursor:'pointer',color:muted,fontSize:18,lineHeight:1}}>✕</button>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:muted,display:'block',marginBottom:5}}>TITLE *</label>
                <input value={editForm.title} onChange={e=>setEditForm(f=>({...f,title:e.target.value}))}
                  style={{width:'100%',padding:'9px 12px',borderRadius:9,border:border,background:darkMode?'rgba(255,255,255,0.05)':'#f8fafc',color:text,fontSize:13,outline:'none',boxSizing:'border-box'}}
                  placeholder="Meeting title"/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:muted,display:'block',marginBottom:5}}>DATE & TIME *</label>
                <input type="datetime-local" value={editForm.scheduled_at} onChange={e=>setEditForm(f=>({...f,scheduled_at:e.target.value}))}
                  style={{width:'100%',padding:'9px 12px',borderRadius:9,border:border,background:darkMode?'rgba(255,255,255,0.05)':'#f8fafc',color:text,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:muted,display:'block',marginBottom:5}}>DURATION (minutes)</label>
                <select value={editForm.duration_minutes} onChange={e=>setEditForm(f=>({...f,duration_minutes:parseInt(e.target.value)}))}
                  style={{width:'100%',padding:'9px 12px',borderRadius:9,border:border,background:darkMode?'rgba(255,255,255,0.05)':'#f8fafc',color:text,fontSize:13,outline:'none',boxSizing:'border-box'}}>
                  {[15,30,45,60,90,120].map(d=><option key={d} value={d}>{d} minutes</option>)}
                </select>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:muted,display:'block',marginBottom:5}}>DESCRIPTION</label>
                <textarea value={editForm.description} onChange={e=>setEditForm(f=>({...f,description:e.target.value}))} rows={3}
                  style={{width:'100%',padding:'9px 12px',borderRadius:9,border:border,background:darkMode?'rgba(255,255,255,0.05)':'#f8fafc',color:text,fontSize:13,outline:'none',resize:'vertical',boxSizing:'border-box'}}
                  placeholder="Optional description"/>
              </div>
              <div>
                <label style={{fontSize:11,fontWeight:600,color:muted,display:'block',marginBottom:5}}>MEETING LINK</label>
                <input value={editForm.meet_link} onChange={e=>setEditForm(f=>({...f,meet_link:e.target.value}))}
                  style={{width:'100%',padding:'9px 12px',borderRadius:9,border:border,background:darkMode?'rgba(255,255,255,0.05)':'#f8fafc',color:text,fontSize:13,outline:'none',boxSizing:'border-box'}}
                  placeholder="https://..."/>
              </div>
              <p style={{fontSize:11,color:muted,margin:0}}>📧 All participants will be notified by email and in-app notification.</p>
              <div style={{display:'flex',gap:10,marginTop:4}}>
                <button onClick={()=>setShowEditModal(false)}
                  style={{flex:1,padding:'10px',borderRadius:10,border:border,background:'transparent',color:text,fontSize:13,fontWeight:600,cursor:'pointer'}}>
                  Cancel
                </button>
                <button onClick={saveMeetingEdit} disabled={editSaving}
                  style={{flex:2,padding:'10px',borderRadius:10,border:'none',background:`linear-gradient(135deg,${accent},#2563eb)`,color:'white',fontSize:13,fontWeight:700,cursor:editSaving?'not-allowed':'pointer',opacity:editSaving?0.7:1}}>
                  {editSaving ? 'Saving...' : '✓ Save & Notify Participants'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
